import { useQuery } from '@tanstack/react-query';
import React, { createContext, useContext, useMemo } from 'react';
import { toast } from 'react-toastify';
import api from '../services/api';
import { useAuth } from './auth';

const UniversitiesContext = createContext({});

const genders = [
  { value: 'male', label: 'Masculino' },
  { value: 'female', label: 'Feminino' },
  { value: 'non-binary', label: 'Não-binário' },
];

export function UniversitiesProvider({ children }) {
  const { user } = useAuth();

  const { data: universities } = useQuery({
    enabled: !!user,
    queryKey: ['universities'],
    placeholderData: [],
    queryFn: () => api.get('/universities/registration').then(res => res.data),
    onError: () => toast.error('Erro ao carregar universidades'),
    staleTime: 1000 * 60 * 60 * 24,
  });

  const { data: apiLocales } = useQuery({
    enabled: !!user,
    queryKey: ['locales'],
    placeholderData: [
      { name: 'pt-br', local_description: 'Português brasileiro', global_description: 'Brazilian portuguese' },
    ],
    queryFn: () => api.get('/locales').then(res => res.data),
    onError: () => toast.error('Erro ao carregar idiomas'),
    staleTime: Infinity,
  });

  const locales = useMemo(
    () => ({
      default: apiLocales?.find(option => option.name === 'pt-br'),
      options: apiLocales,
    }),
    [apiLocales]
  );

  const { data: majors } = useQuery({
    enabled: !!user,
    queryKey: ['majors'],
    placeholderData: [],
    queryFn: () => api.get('/countries/1/majors').then(res => res.data),
    onError: () => toast.error('Erro ao carregar cursos'),
    staleTime: 1000 * 60 * 60 * 24,
  });

  const { data: cities } = useQuery({
    enabled: !!user,
    queryKey: ['cities'],
    placeholderData: [],
    queryFn: () => api.get('/cities').then(res => res.data),
    onError: () => toast.error('Erro ao carregar cidades'),
    staleTime: 1000 * 60 * 60 * 24,
  });

  const { data: events } = useQuery({
    enabled: !!user,
    queryKey: ['events'],
    placeholderData: [],
    queryFn: () => api.get('admin/events').then(res => res.data),
    onError: () => toast.error('Erro ao carregar eventos'),
    staleTime: 1000 * 60 * 60,
  });

  const { data: partners, refetch: refetchPartners } = useQuery({
    enabled: !!user,
    queryKey: ['partners'],
    placeholderData: [],
    queryFn: () => api.get('/admin').then(res => res.data.partners),
    onError: () => toast.error('Erro ao carregar parceiros'),
    staleTime: 1000 * 60 * 60 * 24,
  });

  const { data: states } = useQuery({
    enabled: !!user,
    queryKey: ['states'],
    placeholderData: [],
    queryFn: () => api.get('/states').then(res => res.data.sort((a, b) => a.name.localeCompare(b.name))),
    onError: () => toast.error('Erro ao carregar estados'),
    staleTime: 1000 * 60 * 60 * 24,
  });

  const { data: subscriptions } = useQuery({
    enabled: !!user,
    queryKey: ['subscriptions'],
    placeholderData: [],
    queryFn: () => api.get('/admin/subscription_types').then(res => res.data.sort((a, b) => a.id - b.id)),
    onError: () => toast.error('Erro ao carregar assinaturas'),
    staleTime: 1000 * 60 * 60 * 24,
  });

  const { data: countries } = useQuery({
    enabled: !!user,
    queryKey: ['countries'],
    placeholderData: [],
    queryFn: () => api.get('/countries').then(res => res.data.sort((a, b) => a.id - b.id).slice(0, 2)),
    onError: () => toast.error('Erro ao carregar países'),
    staleTime: Infinity,
  });

  const { data: issues } = useQuery({
    enabled: !!user,
    queryKey: ['issues'],
    placeholderData: [],
    queryFn: () => api.get('/admin/issues').then(res => res.data),
    onError: () => toast.error('Erro ao carregar tickets'),
    staleTime: 1000 * 60 * 5,
  });

  const value = useMemo(() => {
    return {
      cities,
      majors,
      events,
      states,
      universities,
      partners,
      refetchPartners,
      countries,
      locales,
      subscriptions,
      genders,
      issues,
    };
  }, [
    cities,
    majors,
    events,
    partners,
    refetchPartners,
    states,
    subscriptions,
    universities,
    countries,
    locales,
    issues,
  ]);

  return <UniversitiesContext.Provider value={value}>{children}</UniversitiesContext.Provider>;
}

export const useUniversities = () => {
  const context = useContext(UniversitiesContext);

  if (!context) {
    throw new Error('useUniversities must be used within an UniversitiesProvider');
  }

  return context;
};
